import { useTexture } from '@react-three/drei';
import { type GroupProps, useFrame, useThree } from '@react-three/fiber';
import { forwardRef, useEffect, useRef } from 'react';
import {
  Color,
  ColorRepresentation,
  Group,
  PlaneGeometry,
  RepeatWrapping,
  Vector3,
} from 'three';
import { Water as ThreeWater } from 'three-stdlib';

import CloudGenerator from '0_canvas/CloudGenerator/CloudGenerator';
import Rain from '0_canvas/Rain/Rain';
import { useThreeStore } from 'state/threeStore';
import { publicAssets } from 'utils/gcs';

import useRaycast from 'hooks/useRaycast';

export interface WaterPerilProps extends GroupProps {}

const WATER_GEOMETRY = new PlaneGeometry(8000, 8000, 10, 10);
const WATER_POSITION_Y = -3;
const SkyColor = ({ color }: { color: ColorRepresentation }) => {
  const scene = useThree(state => state.scene);
  useEffect(() => {
    scene.background = new Color(color);
  }, [scene, color]);
  return null;
};

const WaterPeril = forwardRef<Group, WaterPerilProps>((props, ref) => {
  const scene = useThree(state => state.scene);
  const { sky } = useThreeStore.getState();

  const waterNormals = useTexture(
    publicAssets('/effects/water-normals.jpeg'),
    tex => {
      tex.wrapS = RepeatWrapping;
      tex.wrapT = RepeatWrapping;
    }
  );

  const water = useRef(
    new ThreeWater(WATER_GEOMETRY, {
      textureWidth: 512,
      textureHeight: 512,
      waterNormals: waterNormals,
      distortionScale: 3.7,
      sunDirection: new Vector3(),
      sunColor: 0xffffff,
      waterColor: 0x001e0f,
      fog: !!scene.fog,
    })
  );

  useRaycast(position => {
    position.y += WATER_POSITION_Y;
    water.current.position.copy(position);
  }, {});

  useFrame(({ clock }) => {
    water.current.material.uniforms.normalSampler.value = waterNormals;
    water.current.material.uniforms.time.value = clock.getElapsedTime();
    water.current.material.uniformsNeedUpdate = true;
  });

  const skyColor = '#6a7676';

  return (
    <group {...props} ref={ref}>
      <Rain />
      <CloudGenerator
        height={220}
        maxRange={600}
        minRange={275}
        pointsPerQuadrant={10}
        color="white"
      />
      {sky === 'FLOOD' && <SkyColor color={skyColor} />}
      <primitive object={water.current} rotation={[-Math.PI / 2, 0, 0]} />
    </group>
  );
});

WaterPeril.displayName = 'WaterPeril';
export default WaterPeril;
